import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from '../hooks/use-toast';

const Register = () => {
  const [searchParams] = useSearchParams();
  // A board-invite email lands here as /register?email=...&board=<id>
  const invitedBoard = searchParams.get('board') || '';
  const [name, setName] = useState('');
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(email, password, name);
      toast({ title: 'Success', description: 'Account created successfully!' });
      // Invited via a board link → open that board straight away (access was
      // granted server-side during registration); otherwise go to workspaces.
      navigate(invitedBoard ? `/boards/${invitedBoard}` : '/workspaces');
    } catch (error) {
      const detail = error.response?.data?.detail;
      // Outside Acuity there's no self sign-up — send them to ask for guest access.
      if (error.response?.status === 403 && /guest access/i.test(detail || '')) {
        navigate('/guest-access', { state: { email, name } });
        toast({ title: 'Guest access needed', description: 'An admin needs to approve access for this email first.' });
        return;
      }
      toast({
        title: 'Error',
        description: detail || 'Failed to create account',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center mb-4">
            <img src="/acuity-logo.png" alt="Acuity Professional" className="h-12 object-contain" data-testid="register-logo" />
          </div>
          <CardTitle className="text-2xl text-center">Create your account</CardTitle>
          <CardDescription className="text-center">
            Get started with Acuity Work Management
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
              />
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700" disabled={loading}>
              {loading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            Already have an account?{' '}
            <Link to="/login" className="text-orange-600 hover:text-orange-700 font-medium">
              Sign in
            </Link>
          </div>
          <div className="mt-2 text-center text-sm text-gray-500">
            Not at Acuity Professional?{' '}
            <Link to="/guest-access" state={{ email, name }} className="text-orange-600 hover:text-orange-700 font-medium" data-testid="register-guest-link">
              Request guest access
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Register;
